/**
 * Sticky Add to Cart - PDP
 * Professional sticky ATC functionality for product detail pages
 */

class StickyATCPDP {
  constructor() {
    this.stickyBar = document.querySelector('[data-sticky-atc]');
    this.stickyButton = document.querySelector('[data-sticky-atc-btn]');
    this.mainAddToCartButton = null;
    this.breadcrumbSection = document.querySelector('.vela-breadcrumbs');
    this.productForm = document.querySelector('form[action*="/cart/add"]');
    this.variantSelect = null;

    // Header reference for "do not cover header at top"
    this.header = document.querySelector('.section-header');
    this.headerHeight = 0;

    if (!this.stickyBar || !this.stickyButton) {
      return;
    }

    this.init();
  }

  init() {
    this.findMainButton();
    this.setHeaderHeight();
    this.setupIntersectionObserver();
    this.setupEventListeners();
    this.positionBelowBreadcrumb();
    this.listenForVariantChanges();

    // Recalculate on resize
    window.addEventListener(
      'resize',
      this.debounce(() => {
        this.setHeaderHeight();
        this.positionBelowBreadcrumb();
        this.applyVisibilityRules();
      }, 250)
    );

    // Also enforce top-of-page behavior on scroll
    window.addEventListener(
      'scroll',
      this.debounce(() => {
        this.applyVisibilityRules();
      }, 25),
      { passive: true }
    );

    // Enforce correct initial state
    this.applyVisibilityRules();
  }

  setHeaderHeight() {
    if (!this.header) {
      this.headerHeight = 0;
      return;
    }
    this.headerHeight = this.header.offsetHeight || 0;
  }

  findMainButton() {
    const selectors = [
      '.product-single__form button[type="submit"]',
      '.product-single__form input[type="submit"]',
      '.product-single__form button[data-custom-atc]',
      '.product-single__form .btn--add-to-cart',
      'button[name="add"]',
      '.product__info-wrapper button[type="submit"]'
    ];

    for (const selector of selectors) {
      this.mainAddToCartButton = document.querySelector(selector);
      if (this.mainAddToCartButton) break;
    }
  }

  positionBelowBreadcrumb() {
    if (!this.breadcrumbSection) return;

    const breadcrumbHeight = this.breadcrumbSection.offsetHeight;
    // this.stickyBar.style.top = `${breadcrumbHeight}px`;
  }

  setupIntersectionObserver() {
    if (!this.mainAddToCartButton) return;

    const options = {
      threshold: 0,
      rootMargin: '-100px 0px 0px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Main button is visible, hide sticky bar
          this.hideStickyBar();
        } else {
          // Main button not visible, try to show sticky bar
          this.showStickyBar();
        }
      });
    }, options);

    observer.observe(this.mainAddToCartButton);
  }

  // Only show after user has scrolled past the header height
  shouldAllowStickyToShow() {
    const y = window.scrollY || window.pageYOffset || 0;
    return y > (this.headerHeight || 0);
  }

  applyVisibilityRules() {
    // If we are near the top, force hide to avoid covering header
    if (!this.shouldAllowStickyToShow()) {
      this.hideStickyBar();
    }
  }

  showStickyBar() {
    if (!this.shouldAllowStickyToShow()) {
      this.hideStickyBar();
      return;
    }

    requestAnimationFrame(() => {
      this.stickyBar.classList.add('is-visible');
    });
  }

  hideStickyBar() {
    requestAnimationFrame(() => {
      this.stickyBar.classList.remove('is-visible');
    });
  }

  setupEventListeners() {
    this.stickyButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleAddToCart();
    });
  }

  async handleAddToCart() {
    if (this.stickyButton.disabled || this.stickyButton.classList.contains('is-loading')) {
      return;
    }

    let variantId = this.stickyButton.dataset.variantId;

    if (!variantId && this.productForm) {
      const variantInput = this.productForm.querySelector('[name="id"]');
      variantId = variantInput ? variantInput.value : null;
    }

    if (!variantId) {
      return;
    }

    this.stickyButton.classList.add('is-loading');
    this.stickyButton.disabled = true;

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      await response.json();

      this.stickyButton.classList.remove('is-loading');
      this.stickyButton.classList.add('is-success');

      await this.updateCart();
      this.openCartDrawer();

      setTimeout(() => {
        this.stickyButton.classList.remove('is-success');
        this.stickyButton.disabled = false;
      }, 1000);
    } catch (error) {
      this.stickyButton.classList.remove('is-loading');
      this.stickyButton.disabled = false;
      alert('There was an issue adding the product to cart. Please try again.');
    }
  }

  async updateCart() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await response.json();

      this.updateCartCount(cart.item_count);

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart }
        })
      );

      return cart;
    } catch (error) {
      // silent
    }
  }

  updateCartCount(count) {
    const cartCountElements = document.querySelectorAll(
      '[data-cart-count], .cart-count, .header__cart-count'
    );
    cartCountElements.forEach((el) => {
      el.textContent = count;
      if (count > 0) {
        el.classList.add('visible');
      }
    });
  }

  openCartDrawer() {
    if (typeof jQuery !== 'undefined') {
      jQuery('body').trigger('drawer.open');
    }

    document.dispatchEvent(new CustomEvent('cart:open'));

    const drawer = document.querySelector('[data-cart-drawer]');
    if (drawer) {
      drawer.classList.add('is-open', 'active');
    }

    const cartDrawerButton = document.querySelector('[data-drawer-toggle="cart"]');
    if (cartDrawerButton) {
      cartDrawerButton.click();
    }
  }

  listenForVariantChanges() {
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.updateVariant(e.detail.variant);
      }
    });

    if (this.productForm) {
      const variantSelects = this.productForm.querySelectorAll('[name="id"]');
      variantSelects.forEach((select) => {
        select.addEventListener('change', (e) => {
          const selectedVariantId = e.target.value;
          this.stickyButton.dataset.variantId = selectedVariantId;

          const selectedOption = e.target.options[e.target.selectedIndex];
          const isAvailable = !selectedOption.disabled;
          this.stickyButton.disabled = !isAvailable;

          if (isAvailable) {
            const price = selectedOption.dataset.price || this.stickyButton.dataset.price;
            this.updateButtonText(price);
          } else {
            const t = this.stickyButton.querySelector('.sticky-atc-pdp__button-text');
            if (t) t.textContent = 'Sold Out';
          }
        });
      });
    }
  }

  updateVariant(variant) {
    if (!variant) return;

    this.stickyButton.dataset.variantId = variant.id;
    this.stickyButton.disabled = !variant.available;

    if (variant.available) {
      this.updatePrice(variant);
      this.updateButtonText(variant.price);
    } else {
      const t = this.stickyButton.querySelector('.sticky-atc-pdp__button-text');
      if (t) t.textContent = 'Sold Out';
    }
  }

  updatePrice(variant) {
    const priceContainer = this.stickyBar.querySelector('.sticky-atc-pdp__price');
    if (!priceContainer) return;

    const formattedPrice = this.formatMoney(variant.price);
    const formattedComparePrice = this.formatMoney(variant.compare_at_price);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceContainer.innerHTML = `
        <span class="sticky-atc-pdp__price-sale">${formattedPrice}</span>
        <span class="sticky-atc-pdp__price-compare">${formattedComparePrice}</span>
      `;
    } else {
      priceContainer.innerHTML = `
        <span class="sticky-atc-pdp__price-regular">${formattedPrice}</span>
      `;
    }
  }

  updateButtonText(price) {
    const buttonText = this.stickyButton.querySelector('.sticky-atc-pdp__button-text');
    if (buttonText) {
      const formattedPrice = this.formatMoney(price);
      buttonText.textContent = `Add to cart - ${formattedPrice}`;
    }
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseFloat(cents);
    }

    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat || '${{amount}}');
    }

    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyATCPDP();
  });
} else {
  new StickyATCPDP();
}